/**
 * Offline Sync — file de mutations (POST/PUT/DELETE) via IndexedDB.
 *
 * Comportement :
 *  - Quand un appel Axios échoue par manque de réseau, on l'enfile dans IndexedDB
 *  - À chaque `online` event ou toutes les 30 s, on rejoue la file dans l'ordre
 *  - On expose `onQueueChange()` pour que la UI affiche le compteur
 *
 * Requêtes GET : le Service Worker s'en charge (cache API stale-while-revalidate).
 */

const DB_NAME = 'gab-offline-sync';
const STORE = 'mutations';
const listeners = new Set();

function openDb() {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, 1);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        const store = db.createObjectStore(STORE, { keyPath: 'id', autoIncrement: true });
        store.createIndex('createdAt', 'createdAt');
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

async function tx(mode) {
  const db = await openDb();
  return db.transaction(STORE, mode).objectStore(STORE);
}

export async function enqueueMutation(mutation) {
  // mutation = { method, url, data, headers, contentType }
  const store = await tx('readwrite');
  return new Promise((resolve, reject) => {
    const rec = { ...mutation, createdAt: new Date().toISOString(), attempts: 0 };
    const req = store.add(rec);
    req.onsuccess = () => { notifyChange(); resolve(req.result); };
    req.onerror = () => reject(req.error);
  });
}

export async function listPending() {
  const store = await tx('readonly');
  return new Promise((resolve, reject) => {
    const items = [];
    const cursor = store.index('createdAt').openCursor();
    cursor.onsuccess = () => {
      const c = cursor.result;
      if (c) { items.push(c.value); c.continue(); } else resolve(items);
    };
    cursor.onerror = () => reject(cursor.error);
  });
}

export async function deletePending(id) {
  const store = await tx('readwrite');
  return new Promise((resolve, reject) => {
    const req = store.delete(id);
    req.onsuccess = () => { notifyChange(); resolve(); };
    req.onerror = () => reject(req.error);
  });
}

export async function countPending(urlPattern) {
  const items = await listPending();
  if (!urlPattern) return items.length;
  return items.filter((it) => (it.url || '').includes(urlPattern)).length;
}

function notifyChange() {
  // Charge une seule fois la liste puis calcule les compteurs par listener
  listPending().then((items) => {
    listeners.forEach((entry) => {
      try {
        const n = entry.pattern
          ? items.filter((it) => (it.url || '').includes(entry.pattern)).length
          : items.length;
        entry.fn(n);
      } catch { /* noop */ }
    });
  });
}

export function onQueueChange(fn, pattern) {
  const entry = { fn, pattern: pattern || null };
  listeners.add(entry);
  countPending(pattern).then(fn).catch(() => {});
  return () => listeners.delete(entry);
}

/**
 * flushQueue — rejoue toutes les mutations en attente. Idempotent, safe à appeler souvent.
 */
let flushing = false;
export async function flushQueue(axiosInstance) {
  if (flushing || !navigator.onLine) return { attempted: 0, ok: 0 };
  flushing = true;
  let attempted = 0, ok = 0;
  try {
    const items = await listPending();
    for (const it of items) {
      attempted++;
      try {
        const cfg = {
          method: it.method,
          url: it.url,
          data: it.data,
          headers: it.headers || {},
        };
        await axiosInstance.request(cfg);
        await deletePending(it.id);
        ok++;
      } catch (e) {
        const status = e.response?.status;
        // Erreurs client (4xx sauf 408/429) : la requête est cassée, on la retire
        if (status && status >= 400 && status < 500 && status !== 408 && status !== 429) {
          await deletePending(it.id);
        }
        // sinon on la garde pour retry ultérieur
      }
    }
  } finally {
    flushing = false;
    notifyChange();
  }
  return { attempted, ok };
}

/** Installe l'intercepteur axios : enfile les mutations échouées pour cause réseau. */
export function attachOfflineInterceptor(axiosInstance) {
  axiosInstance.interceptors.response.use(
    (r) => r,
    async (error) => {
      const cfg = error.config || {};
      const method = (cfg.method || 'get').toLowerCase();
      const isWrite = ['post', 'put', 'patch', 'delete'].includes(method);
      const isNetworkErr = !error.response;
      // Ne pas enfiler les mutations d'auth (login, logout)
      const isAuthUrl = (cfg.url || '').includes('/auth/');
      if (isWrite && isNetworkErr && !isAuthUrl) {
        try {
          await enqueueMutation({
            method,
            url: cfg.url,
            data: cfg.data,
            headers: cfg.headers,
            contentType: cfg.headers?.['Content-Type'],
          });
          // Retourne une "fausse" réponse pour ne pas bloquer l'UI
          return Promise.resolve({
            data: { queued: true, offline: true },
            status: 202,
            statusText: 'Queued for sync',
            config: cfg,
            offline: true,
          });
        } catch { /* fallthrough */ }
      }
      return Promise.reject(error);
    }
  );

  // Rejoue automatiquement à la reconnexion + toutes les 30 s
  window.addEventListener('online', () => flushQueue(axiosInstance));
  setInterval(() => {
    if (navigator.onLine) flushQueue(axiosInstance);
  }, 30000);
}
